import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Box,
  Chip,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  InputAdornment,
} from '@mui/material';
import {
  Search as SearchIcon,
  Upgrade as UpgradeIcon,
  Block as BlockIcon,
} from '@mui/icons-material';
import api from '../../utils/api';
import { useNotification } from '../../contexts/NotificationContext';
import { useAuth } from '../../contexts/AuthContext';

const ManageSuperuserStatus: React.FC = () => {
  const { showSuccess, showError } = useNotification();
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<any[]>([]);
  const [count, setCount] = useState<number>(0);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState<string>('');
  const [searchInput, setSearchInput] = useState<string>('');
  const [superuserFilter, setSuperuserFilter] = useState<string>('all');
  const [actionLoading, setActionLoading] = useState<number | null>(null);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.append('page', String(page + 1));
      params.append('page_size', String(rowsPerPage));
      if (search.trim()) {
        params.append('search', search.trim());
      }
      if (superuserFilter !== 'all') {
        params.append('is_superuser', superuserFilter === 'true' ? 'true' : 'false');
      }

      const response = await api.get(`/users/list_all_for_superuser/?${params.toString()}`);
      
      if (response.data.results) {
        setUsers(response.data.results);
        setCount(response.data.count || 0);
      } else {
        setUsers(response.data);
        setCount(response.data.length);
      }
    } catch (err: any) {
      setError('Failed to load users. Please try again.');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, rowsPerPage, search, superuserFilter]);

  const handleSearch = () => {
    setSearch(searchInput);
    setPage(0); // Reset to first page when searching
  };

  const handleSearchKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleToggleSuperuser = async (userId: number, currentStatus: boolean) => {
    if (!currentUser || !currentUser.is_superuser) {
      showError('You do not have permission to perform this action.');
      return;
    }

    // Prevent users from changing their own status
    if (userId === currentUser.id) {
      showError('You cannot change your own superuser status.');
      return;
    }

    setActionLoading(userId);
    try {
      // Use PATCH to update the user's superuser status directly
      const response = await api.patch(`/users/${userId}/`, {
        is_superuser: !currentStatus,
      });

      if (response.data) {
        showSuccess(
          response.data.message || 
          `User ${response.data.email} ${!currentStatus ? 'upgraded' : 'downgraded'} to ${!currentStatus ? 'superuser' : 'regular user'} status.`
        );
        fetchUsers(); // Refresh the list
      }
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.detail ||
        err.response?.data?.message ||
        'Failed to update user status. Please try again.';
      showError(errorMessage);
    } finally {
      setActionLoading(null);
    }
  };

  const handlePageChange = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Manage User Superuser Status
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        View and manage superuser status for all users across all companies.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters and Search */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          placeholder="Search users..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyPress={handleSearchKeyPress}
          size="small"
          sx={{ minWidth: 250 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <Button variant="outlined" onClick={handleSearch} size="small">
          Search
        </Button>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id="superuser-filter-label">Filter by Status</InputLabel>
          <Select
            labelId="superuser-filter-label"
            label="Filter by Status"
            value={superuserFilter}
            onChange={(e) => {
              setSuperuserFilter(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="all">All Users</MenuItem>
            <MenuItem value="true">Superusers Only</MenuItem>
            <MenuItem value="false">Regular Users Only</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Table */}
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Company</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Superuser Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress sx={{ my: 2 }} />
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                    No users found.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    {user.first_name} {user.last_name}
                  </TableCell>
                  <TableCell>{user.company?.name || 'N/A'}</TableCell>
                  <TableCell>
                    <Chip
                      label={user.role?.charAt(0).toUpperCase() + user.role?.slice(1) || 'N/A'}
                      size="small"
                      color={
                        user.role === 'admin'
                          ? 'error'
                          : user.role === 'manager'
                          ? 'warning'
                          : 'info'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={user.is_superuser ? 'Superuser' : 'Regular User'}
                      size="small"
                      color={user.is_superuser ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {user.id === currentUser?.id ? (
                      <Typography variant="caption" color="text.secondary">
                        (You)
                      </Typography>
                    ) : (
                      <IconButton
                        size="small"
                        onClick={() => handleToggleSuperuser(user.id, user.is_superuser)}
                        disabled={actionLoading === user.id}
                        color={user.is_superuser ? 'error' : 'primary'}
                        title={user.is_superuser ? 'Remove superuser status' : 'Grant superuser status'}
                      >
                        {actionLoading === user.id ? (
                          <CircularProgress size={20} />
                        ) : user.is_superuser ? (
                          <BlockIcon fontSize="small" />
                        ) : (
                          <UpgradeIcon fontSize="small" />
                        )}
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        component="div"
        count={count}
        page={page}
        onPageChange={handlePageChange}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        rowsPerPageOptions={[10, 25, 50, 100]}
      />
    </Paper>
  );
};

export default ManageSuperuserStatus;
